/** 場地當日可用性格（主辦／職員活動上架共用邏輯） */

function parseHmToMinutes(hm) {
  const m = String(hm || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
}

function minsToHm(mins) {
  const m = ((mins % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * @returns {{ slots: Array<{time:string, available:boolean, unavailableReason?:string, clickable?:boolean}>, interval: number }}
 */
export function buildDayAvailabilitySlots(availability, dateStr) {
  if (!availability || !dateStr) return { slots: [], interval: 30 };
  const product = availability.product || {};
  const interval = Number(product.timeSlotInterval || product.TIME_SLOT_INTERVAL) || 30;
  const openMin = parseHmToMinutes(product.timeSlotStart || product.TIME_SLOT_START || '10:00') ?? 10 * 60;
  const closeMin = parseHmToMinutes(product.timeSlotEnd || product.TIME_SLOT_END || '22:00') ?? 22 * 60;
  if (!(closeMin > openMin) || !(interval > 0)) return { slots: [], interval };

  const bookedRaw = [
    ...(availability.bookedSlots || []),
    ...(availability.exclusionBookedSlots || []),
  ];
  const busyRanges = bookedRaw
    .map((b) => {
      const slotDate = b.slotDate || b.SLOT_DATE || String(b.startAt || b.START_AT || '').slice(0, 10);
      let s = parseHmToMinutes(b.startTime || b.START_TIME);
      let e = parseHmToMinutes(b.endTime || b.END_TIME);
      if (s == null && (b.startAt || b.START_AT)) s = parseHmToMinutes(String(b.startAt || b.START_AT).slice(11, 16));
      if (e == null && (b.endAt || b.END_AT)) e = parseHmToMinutes(String(b.endAt || b.END_AT).slice(11, 16));
      if (s == null || e == null) return null;
      const startDay = String(b.startAt || b.START_AT || '').slice(0, 10) || slotDate;
      const endDay = String(b.endAt || b.END_AT || '').slice(0, 10) || slotDate;
      if (startDay !== dateStr && endDay !== dateStr && slotDate !== dateStr) return null;
      let absS = s;
      let absE = e <= s ? e + 24 * 60 : e;
      if (startDay === dateStr && endDay && endDay !== dateStr) absE = 24 * 60;
      if (endDay === dateStr && startDay && startDay !== dateStr) absS = 0;
      const clippedS = Math.max(absS, openMin);
      const clippedE = Math.min(absE, closeMin);
      if (clippedE <= clippedS) return null;
      return { s: clippedS, e: clippedE };
    })
    .filter(Boolean);

  const slots = [];
  for (let m = openMin; m < closeMin; m += interval) {
    const time = minsToHm(m);
    const busy = busyRanges.some((r) => m >= r.s && m < r.e);
    slots.push({
      time,
      available: !busy,
      unavailableReason: busy ? 'booked' : undefined,
    });
  }
  return { slots, interval };
}

/** 月曆每日：open / partial / full */
export function buildMonthDayStatus(availability, monthYm) {
  const map = {};
  if (!availability || !monthYm) return map;
  const [y, m] = String(monthYm).split('-').map(Number);
  if (!y || !m) return map;
  const daysInMonth = new Date(y, m, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${monthYm}-${String(d).padStart(2, '0')}`;
    const { slots } = buildDayAvailabilitySlots(availability, dateStr);
    if (!slots.length) {
      map[dateStr] = 'full';
      continue;
    }
    const avail = slots.filter((s) => s.available).length;
    if (avail === 0) map[dateStr] = 'full';
    else if (avail === slots.length) map[dateStr] = 'open';
    else map[dateStr] = 'partial';
  }
  return map;
}

function rangeContinuousAvailable(slots, startIdx, endIdx) {
  if (startIdx < 0 || endIdx < startIdx || endIdx >= slots.length) return false;
  for (let i = startIdx; i <= endIdx; i++) {
    if (!slots[i]?.available) return false;
  }
  return true;
}

/**
 * 兩段式點選：尚未選開始 → 可點所有空檔；已選開始 → 可點開始之後任一格作為結束
 * （結束不受開放／空檔連續限制；開始仍須為空檔）。
 * 結束時間 = 該格開始 + interval。
 */
export function withRangePickClickable(slots, selectedStart) {
  if (!slots?.length) return [];
  const startIdx = selectedStart
    ? slots.findIndex((s) => s.time === selectedStart && !s.nextDay)
    : -1;
  return slots.map((s, idx) => {
    if (startIdx < 0) {
      return { ...s, clickable: !!s.available };
    }
    if (idx < startIdx) return { ...s, clickable: false };
    return { ...s, clickable: true };
  });
}

/**
 * @returns {{ start: string, end: string, phase: 'start'|'end'|'done' }}
 */
export function applyRangeSlotClick({ slots, interval, selectedStart, selectedEnd, slot }) {
  const list = slots || [];
  const idx = list.findIndex((s) => s.time === slot.time && !s.nextDay);
  if (idx < 0) {
    return { start: selectedStart || '', end: selectedEnd || '', phase: selectedStart ? (selectedEnd ? 'done' : 'end') : 'start' };
  }
  const step = interval > 0 ? interval : 30;

  if (!selectedStart || (selectedStart && selectedEnd)) {
    if (!list[idx]?.available) {
      return { start: selectedStart || '', end: selectedEnd || '', phase: selectedStart ? (selectedEnd ? 'done' : 'end') : 'start' };
    }
    return { start: slot.time, end: '', phase: 'end' };
  }

  const startIdx = list.findIndex((s) => s.time === selectedStart && !s.nextDay);
  if (startIdx < 0) {
    if (!list[idx]?.available) {
      return { start: '', end: '', phase: 'start' };
    }
    return { start: slot.time, end: '', phase: 'end' };
  }

  if (idx === startIdx) {
    const startMin = parseHmToMinutes(selectedStart);
    const endHm = minsToHm((startMin ?? 0) + step);
    return { start: selectedStart, end: endHm, phase: 'done' };
  }

  if (idx < startIdx) {
    if (!list[idx]?.available) {
      return { start: selectedStart, end: '', phase: 'end' };
    }
    return { start: slot.time, end: '', phase: 'end' };
  }

  const endSlotMin = parseHmToMinutes(list[idx].time);
  const endHm = minsToHm((endSlotMin ?? 0) + step);
  return { start: selectedStart, end: endHm, phase: 'done' };
}

/** 可選開始時間（空檔格） */
export function allowedStartTimes(slots) {
  return (slots || []).filter((s) => s.available).map((s) => s.time);
}

/**
 * 開始時間須保證後續 durationMin（預設 4h）內「有格子」的時段皆空（含跨午夜）。
 * 營業時間外、格子不存在的段落略過（由後端衝突檢查把關）。
 */
export function allowedStartTimesForDuration(slots, durationMin = 4 * 60, interval, nextDaySlots = []) {
  const step = interval > 0 ? interval : 30;
  const dayCap = 24 * 60;
  const list0 = slots || [];
  const list1 = nextDaySlots || [];
  const slotAt = (list, clockMin) => list.find((x) => !x.nextDay && parseHmToMinutes(x.time) === clockMin);
  return list0
    .filter((s) => {
      if (!s.available || s.nextDay) return false;
      const startM = parseHmToMinutes(s.time);
      if (startM == null) return false;
      const endAbs = startM + durationMin;
      for (let t = startM; t < endAbs; t += step) {
        const day = Math.floor(t / dayCap);
        const clock = t % dayCap;
        const list = day === 0 ? list0 : (day === 1 ? list1 : null);
        if (!list) return false;
        const cell = slotAt(list, clock);
        if (!cell) continue;
        if (!cell.available) return false;
      }
      return true;
    })
    .map((s) => s.time);
}

/** 相對已選開始的可結束時間（連續空檔邊界）
 *  @param {{ treatStartAsAvailable?: boolean }} [opts] 編輯既有時段時，開始格可能被自身訂單佔用
 */
export function allowedEndTimes(slots, selectedStart, interval, opts = {}) {
  if (!selectedStart || !slots?.length) return [];
  const startIdx = slots.findIndex((s) => s.time === selectedStart && !s.nextDay);
  if (startIdx < 0) return [];
  if (!slots[startIdx]?.available && !opts.treatStartAsAvailable) return [];
  const step = interval > 0 ? interval : 30;
  const out = [];
  for (let i = startIdx; i < slots.length; i++) {
    let ok = true;
    for (let j = startIdx; j <= i; j++) {
      if (j === startIdx && opts.treatStartAsAvailable) continue;
      if (!slots[j]?.available) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    const endMin = (parseHmToMinutes(slots[i].time) ?? 0) + step;
    out.push(minsToHm(endMin));
  }
  return out;
}

export { parseHmToMinutes, minsToHm };
